import { getEpaFormBox } from "encamp-shared/src/waste/discrepancyFieldMeta";
import { type BulkManifestDiscrepanciesQuery, DiscrepancyStatus } from "generated-graphql/graphql";

import { getFieldLabel, sortTerminalLast } from "./DiscrepancyReviewDialog.helpers";

type Discrepancy = BulkManifestDiscrepanciesQuery["bulkManifestDiscrepancies"][number];

/** chevron, checkbox, [Facility, Manifest], Waste line, e-Manifest, Vendor, Document, Status. */
export function getColCount(showManifestColumns: boolean): number {
  return showManifestColumns ? 9 : 7;
}

export interface WasteLineDescriptionFields {
  wasteLineUnifiedId?: string | null;
  wasteLineNumber?: string | null;
  wasteLineDotDescription?: string | null;
  field: string;
}

/**
 * A conflict row's subject line: which waste line the finding is about (or
 * "Manifest" for a manifest-level field), the line's shipping description,
 * and its EPA Form 8700-22 box — e.g. `Line 4 · UN1993 Waste flammable
 * liquid n.o.s. · Box 19`.
 */
export function describeWasteLine(d: WasteLineDescriptionFields): {
  primary: string;
  secondary: string | null;
} {
  const primary = d.wasteLineUnifiedId ? `Line ${d.wasteLineNumber ?? "item"}` : "Manifest";
  const parts: string[] = [];
  if (d.wasteLineDotDescription) parts.push(d.wasteLineDotDescription);
  const box = getEpaFormBox(d.field);
  if (box != null) parts.push(`Box ${box}`);
  return { primary, secondary: parts.length > 0 ? parts.join(" · ") : null };
}

/**
 * A field group's shared held-back reason: the reason every row in the group
 * agrees on, or null when any row lacks one or they disagree.
 *
 * getFieldDiscrepancyRows writes the reason on every per-line row of a
 * manifest whose two sides report different waste line counts, so a group
 * whose rows all come from such a manifest folds under it.
 */
export function getGroupHeldBackReason(
  items: readonly { heldBackReason?: string | null }[]
): string | null {
  let reason: string | null = null;
  for (const item of items) {
    if (item.heldBackReason == null) return null;
    if (reason == null) {
      reason = item.heldBackReason;
    } else if (item.heldBackReason !== reason) {
      return null;
    }
  }
  return reason;
}

export interface FieldGroup {
  field: string;
  label: string;
  items: Discrepancy[];
  heldBackReason: string | null;
}

export function buildFieldGroups(discrepancies: readonly Discrepancy[]): FieldGroup[] {
  const groups = new Map<string, Discrepancy[]>();
  for (const d of discrepancies) {
    const existing = groups.get(d.field);
    if (existing) {
      existing.push(d);
    } else {
      groups.set(d.field, [d]);
    }
  }
  return [...groups.entries()].map(([field, items]) => ({
    field,
    label: getFieldLabel(field),
    items: sortTerminalLast(items),
    heldBackReason: getGroupHeldBackReason(items),
  }));
}

/** The schema documents a null verdict as "not yet recomputed" — never fall back to comparing raw values here. */
export function verdictText(value: boolean | null | undefined): string {
  if (value == null) return "not yet recomputed";
  return value ? "yes" : "no";
}

export interface UnmatchableLinesSummary {
  /** Rows that cannot take a vendor correction until their manifest's lines are matched. */
  rowCount: number;
  /** The manifests those rows are on, in the order they first appear. */
  manifestTrackingNumbers: string[];
  /** The same manifests by id, for matching against what has just been answered. */
  processedManifestIds: string[];
  /**
   * What each side counted, when the warning is about a single manifest. Read
   * off that manifest's wasteLinesCount finding, which is where the two totals
   * already live. Null when several manifests are involved, because they each
   * have their own pair of numbers.
   */
  lineCounts: { eManifest: string; vendor: string } | null;
}

/**
 * The two sides' waste line totals for one manifest, taken from the
 * wasteLinesCount finding that reports them differing.
 */
function lineCountsFor(
  discrepancies: readonly Discrepancy[],
  processedManifestId: string
): { eManifest: string; vendor: string } | null {
  const row = discrepancies.find(
    (d) =>
      d.processedManifestId === processedManifestId &&
      d.field === "wasteLinesCount" &&
      d.vendorDiscrepant === true
  );
  const eManifest = row?.eManifestValue;
  const vendor = row?.vendorValue;
  if (eManifest == null || vendor == null) return null;
  return { eManifest, vendor };
}

/**
 * An open vendor Waste Line Count finding on a manifest with no stored pairing.
 * Catches the manifests the suspect mark misses: rows computed before the mark
 * existed, and a manifest whose pairing was just removed.
 */
function isUnmatchedLineCountFinding(d: Discrepancy): boolean {
  return (
    d.field === "wasteLinesCount" &&
    d.vendorDiscrepant === true &&
    d.status === DiscrepancyStatus.Unresolved &&
    d.vendorWasteLinesMatched === false
  );
}

function isOpenUnmatchedLineRow(d: Discrepancy): boolean {
  return d.vendorWasteLinesUnmatched === true && d.status === DiscrepancyStatus.Unresolved;
}

/**
 * Whether a row belongs to a manifest whose waste lines need matching. An
 * open per-line row the server flags as unmatched counts even when the Waste
 * Line Count finding was dismissed or is pending, since Correct Vendor Data
 * refuses that row until the lines are matched.
 */
function isOnUnmatchableManifest(d: Discrepancy): boolean {
  return (
    d.vendorAlignmentSuspect === true || isOpenUnmatchedLineRow(d) || isUnmatchedLineCountFinding(d)
  );
}

/**
 * The rows sitting on a manifest whose waste lines could not be matched by line
 * number, so the section can say so before anyone opens a row.
 *
 * On these manifests the per-line value comparisons are not reported at all:
 * they would measure one waste against a different one. What is left is the
 * finding naming each line the other side does not have. A reviewer working
 * down the list has usually picked an action before expanding anything, so the
 * warning and the way to fix it belong at the top.
 *
 * Returns null when there is nothing to say, which is the ordinary case.
 */
export function summarizeUnmatchableLines(
  discrepancies: readonly Discrepancy[]
): UnmatchableLinesSummary | null {
  const manifestTrackingNumbers: string[] = [];
  const processedManifestIds: string[] = [];
  let rowCount = 0;

  for (const d of discrepancies) {
    if (!isOnUnmatchableManifest(d)) continue;
    rowCount += 1;
    const mtn = d.manifestTrackingNumber;
    if (mtn != null && !manifestTrackingNumbers.includes(mtn)) {
      manifestTrackingNumbers.push(mtn);
    }
    if (!processedManifestIds.includes(d.processedManifestId)) {
      processedManifestIds.push(d.processedManifestId);
    }
  }

  if (rowCount === 0) return null;

  const [onlyManifestId] = processedManifestIds;
  const lineCounts =
    processedManifestIds.length === 1 && onlyManifestId != null
      ? lineCountsFor(discrepancies, onlyManifestId)
      : null;

  return { rowCount, manifestTrackingNumbers, processedManifestIds, lineCounts };
}

/**
 * Manifests whose waste lines a reviewer has already matched.
 *
 * Reads the stored pairing via `vendorWasteLinesMatched` instead of the
 * `vendorAlignmentSuspect` marks: those also clear on pre-deploy rows and
 * right after a reviewer removes a pairing, neither of which is settled.
 */
export function manifestsWithSettledWasteLineMatch(
  discrepancies: readonly Discrepancy[]
): string[] {
  const settled: string[] = [];
  for (const d of discrepancies) {
    if (d.field !== "wasteLinesCount" || d.vendorDiscrepant !== true) continue;
    if (d.vendorWasteLinesMatched !== true) continue;
    if (!settled.includes(d.processedManifestId)) settled.push(d.processedManifestId);
  }
  return settled;
}

type WasteLineMatchingTargetResult =
  | { processedManifestId: string; manifestTrackingNumber: string | null | undefined }
  | undefined;

/**
 * The first row worth sending the reader to match: the manifest-level
 * wasteLinesCount finding, or — in line-item mode, where the caller has
 * already filtered `discrepancies` down to rows naming a selected waste line
 * and dropped that manifest-level row along with it — any per-line row a
 * vendor alignment made suspect. Either kind still carries its own
 * `processedManifestId` and `manifestTrackingNumber`.
 */
function firstMatchingRow(discrepancies: readonly Discrepancy[]): WasteLineMatchingTargetResult {
  const row =
    discrepancies.find((d) => d.field === "wasteLinesCount" && d.vendorDiscrepant === true) ??
    discrepancies.find((d) => d.vendorAlignmentSuspect === true) ??
    discrepancies.find(isOpenUnmatchedLineRow);
  if (!row) return undefined;
  return {
    processedManifestId: row.processedManifestId,
    manifestTrackingNumber: row.manifestTrackingNumber,
  };
}

/**
 * The manifest the section's warning sends the reader to match.
 *
 * Only the vendor axis: the document comparator raises the same
 * wasteLinesCount field when only the document count differs, and matching
 * vendor lines has no answer for that one.
 *
 * Skips whatever is in `justMatchedIds` first, so a bulk review advances to
 * the next affected manifest instead of reopening one already matched this
 * session. Once every affected manifest is in `justMatchedIds`, the warning
 * has already settled into "Waste lines matched" (both read the same
 * suspect rows), and this falls back to the first manifest overall — which
 * is what "Change matching" on that settled notice reopens.
 */
export function findWasteLineMatchingTarget(
  discrepancies: readonly Discrepancy[],
  justMatchedIds: ReadonlySet<string> = new Set()
): WasteLineMatchingTargetResult {
  const unmatched = discrepancies.filter((d) => !justMatchedIds.has(d.processedManifestId));
  return firstMatchingRow(unmatched) ?? firstMatchingRow(discrepancies);
}

/**
 * Headline for the section warning: the plain fact the reviewer has to act on,
 * which is that the two sides counted different numbers of waste lines. The
 * count of affected conflicts is a consequence of that, not the finding, so it
 * moves to the sentence below.
 */
export function unmatchableLinesHeadline(summary: UnmatchableLinesSummary): string {
  if (summary.manifestTrackingNumbers.length > 1) {
    return `${summary.manifestTrackingNumbers.length} manifests have a different number of waste lines than their vendor data`;
  }
  if (summary.lineCounts) {
    return `e-Manifest has ${summary.lineCounts.eManifest} waste lines, vendor data has ${summary.lineCounts.vendor}`;
  }
  return "e-Manifest and vendor data have a different number of waste lines";
}

/**
 * What that means for the findings below. One sentence: the headline already
 * gives the counts, the button already says what to do, and the manifest is
 * named in the dialog's own title.
 */
export function unmatchableLinesDetail(summary: UnmatchableLinesSummary): string {
  const subject = summary.manifestTrackingNumbers.length > 1 ? "These manifests' lines" : "Lines";
  return `${subject} are not compared until they are matched.`;
}
